/**
 * SN_MTL_MTI_REG_002_POP_002 — 재고적용 (대체출고등록(통합) 전용)
 *
 * 현재고를 조회해 체크한 품목을 상세정보로 내려보낸다 — 출고요청을 안 거치는 길, 요청적용(POP_001)과 짝이다.
 * 대체출고는 입 · 출고 창고를 고정하지 않으므로 창고를 넘기지 않는다(사업장만).
 * getOkayData 는 체크행만 돌려주고 행 추가 · 수량 배분은 화면(btnApplyInv)이 맡는다 — 그래서 수량 입력이 없다.
 * 그 건에 이미 들어간 품목 · 창고는 화면이 excludeKeys 로 넘겨 주고 조회 결과에서 뺀다(저장 전이라 쿼리로는 못 거른다).
 * 조회 sn_mtl_mti_reg_002_pop_002_01 · 조건과 컬럼은 sys_popupctrl · sys_popupgridcolumn 에서 온다.
 */
import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import SearchPanel from "@widgets/SearchPanel";
import { fnGetDataSet } from "@shared/api/restApi";
import { getInitPopUpData } from "@shared/utils/CommonUtils";
import SnGrid from "@shared/components/GridV2/SnGrid";
import { SnGridRef } from "@shared/components/GridV2/SnGridTypes";
import SnSearchBtn from "@shared/components/SnSearchBtn";

interface popupProps {
  selectedData?: any;
  modalRef?: any;
  /**
   * 이미 그 건에 들어가 있는 `${cd_item}\u0000${cd_wh}` 키. 조회 결과에서 뺀다.
   *
   * 저장 전 라인은 DB 에 없으므로(신규 건은 no_io 도 비어 있다) 쿼리로는 거를 수 없다.
   * 그래서 화면이 들고 있는 목록을 받아 여기서 뺀다.
   */
  excludeKeys?: Set<string>;
}

const SN_MTL_MTI_REG_002_POP_002 = forwardRef(
  ({ selectedData, modalRef, excludeKeys }: popupProps, ref: React.Ref<any>) => {
    const gridRefs = useRef<{ [key: string]: React.RefObject<SnGridRef> }>({
      grid1: React.createRef<SnGridRef>(),
    });

    const getGridCurrent = (key: string): SnGridRef => {
      const grid = gridRefs.current[key]?.current;
      if (!grid) {
        throw new Error(`Grid '${key}' is not mounted yet`);
      }
      return grid;
    };

    const searchRef = useRef<any>(null);

    const [formData, setFormData] = useState<any>();
    const [formControlData, setFormControlData] = useState<any[]>([]);
    const [columns, setColums] = useState<any>({
      grid1: [],
    });
    const [dataSources, setDataSources] = useState<any>({
      grid1: [],
    });

    useImperativeHandle(ref, () => ({
      getOkayData: () => {
        return {
          rtnData: [getGridCurrent("grid1").getCheckedRows()],
        };
      },
      onSearch: () => onSearch(),
    }));

    useEffect(() => {
      const fetchform = async () => {
        try {
          const rtn: any = await getInitPopUpData(
            "SN_MTL_MTI_REG_002_POP_002",
            {}
          );
          const mergedData: any = { ...rtn.formData, ...selectedData };
          setFormControlData(rtn.formControlData);
          setFormData(mergedData);
          setColums({
            grid1: rtn.grid.grid1,
          });
        } catch (error) {}
      };
      fetchform();
    }, []);

    const onSearch = async () => {
      try {
        const param = {
          id: "sn_mtl_mti_reg_002_pop_002_01",
          params: searchRef.current.getFormdata(),
        };
        getGridCurrent("grid1").resetUpdate();

        const data: any = await fnGetDataSet(param);
        const rows: any[] = data[0] || [];
        setDataSources({
          grid1: excludeKeys?.size
            ? rows.filter(
                (r) => !excludeKeys.has(`${r.cd_item}\u0000${r.cd_wh}`)
              )
            : rows,
        });
      } catch (error: any) {}
    };

    const grids = useMemo(
      () => ({
        grid1: {
          columns: columns.grid1,
          dataSource: dataSources.grid1,
        },
      }),
      [columns, dataSources]
    );

    return (
      <div>
        <div>
          <SearchPanel
            ref={searchRef}
            data={formData}
            controls={formControlData}
            popupRef={ref}
          ></SearchPanel>
        </div>
        <div style={{ textAlign: "center" }}>
          <SnSearchBtn
            text={"조회"}
            onClick={onSearch}
            sx={{
              width: 80,
              marginTop: 1.5,
            }}
            spinner={true}
          />
        </div>
        <div>
          <div style={{ height: "40vh", marginTop: "10px" }}>
            <SnGrid
              ref={gridRefs.current["grid1"]}
              columnConfig={grids.grid1.columns}
              rowData={grids.grid1.dataSource}
              etcProps={{
                rowSelection: "multiple",
                isEditable: false,
                ischeckboxSelection: true,
              }}
            />
          </div>
        </div>
      </div>
    );
  }
);

export default SN_MTL_MTI_REG_002_POP_002;
